import { useTranslation } from 'react-i18next'
import type { ReconciliationSession } from '../types'
import { DOC_LABEL } from './doc-constants'

type DocViewer = 'ordrsp' | 'desadv' | 'invoice'

export interface XmlTarget {
  xpaths: Partial<Record<DocViewer, string>>
  label: string
}

interface Warning {
  message: string
  xmlTarget?: XmlTarget
}

interface ErrorItem {
  docKey: string
  message: string
  xmlTarget?: XmlTarget
}

interface Props {
  session: ReconciliationSession
  onInfoClick?: (target: XmlTarget) => void
}

function InfoButton({ target, onClick }: { target: XmlTarget; onClick: (t: XmlTarget) => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={() => onClick(target)}
      className="shrink-0 opacity-40 hover:opacity-100 transition-opacity rounded-full align-middle ml-1"
      title={t('validation_summary.show_in_xml', { label: target.label })}
      aria-label={t('validation_summary.show_in_xml', { label: target.label })}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
      </svg>
    </button>
  )
}

const DOC_VIEWER_KEY: Record<string, DocViewer> = {
  order_response: 'ordrsp',
  despatch_advice: 'desadv',
  invoice: 'invoice',
}

const ROOT_ELEM: Record<string, string> = {
  order_response: 'OrderResponse',
  despatch_advice: 'DespatchAdvice',
  invoice: 'Invoice',
}

export function ValidationSummary({ session, onInfoClick }: Props) {
  const { t } = useTranslation()
  const errors: ErrorItem[] = []
  const warnings: Warning[] = []

  // Incomplete-chain: ontbrekende documenttypes in de aangeleverde set — een objectief
  // feit, geen fout/waarschuwing. Zie XMLInsights-SDD requirements/chain-consistency.md
  // REQ-CHAIN-CONSISTENCY-008 / requirements/processing-profiles.md REQ-PROCESSING-PROFILES-013.
  const missingStages = [
    session.missing_order_response  ? DOC_LABEL.order_response  : null,
    session.missing_despatch_advice ? DOC_LABEL.despatch_advice : null,
    session.missing_invoice         ? DOC_LABEL.invoice         : null,
  ].filter((label): label is string => label !== null)

  const checks = [
    {
      docKey: 'order_response',
      present: session.ordrsp_filename !== null,
      missingProject: session.ordrsp_missing_project,
      skipped: session.ordrsp_skipped_lines,
      missingSupplierGln: session.ordrsp_missing_supplier_gln,
      missingBuyerGln: session.ordrsp_missing_buyer_gln,
    },
    {
      docKey: 'despatch_advice',
      present: session.desadv_filename !== null,
      missingProject: session.desadv_missing_project,
      skipped: session.desadv_skipped_lines,
      missingSupplierGln: session.desadv_missing_supplier_gln,
      missingBuyerGln: session.desadv_missing_buyer_gln,
    },
    {
      docKey: 'invoice',
      present: session.invoice_filename !== null,
      missingProject: session.invoice_missing_project,
      skipped: session.invoice_skipped_lines,
      missingSupplierGln: session.invoice_missing_supplier_gln,
      missingBuyerGln: session.invoice_missing_buyer_gln,
    },
  ]

  for (const c of checks) {
    if (!c.present) continue
    const dv = DOC_VIEWER_KEY[c.docKey]
    const re = ROOT_ELEM[c.docKey]
    if (c.missingProject) {
      errors.push({
        docKey: c.docKey,
        message: t('validation_summary.error_missing_project_number'),
        xmlTarget: { xpaths: { [dv]: `${re}/ProjectNumber` } as Partial<Record<DocViewer, string>>, label: 'ProjectNumber' },
      })
    }
    if ((c.skipped ?? 0) > 0) {
      errors.push({ docKey: c.docKey, message: t('validation_summary.error_skipped_lines', { count: c.skipped ?? 0 }) })
    }
    if (c.missingSupplierGln) {
      errors.push({
        docKey: c.docKey,
        message: t('validation_summary.error_missing_supplier_gln'),
        xmlTarget: { xpaths: { [dv]: `${re}/Supplier/GLN` } as Partial<Record<DocViewer, string>>, label: 'Supplier/GLN' },
      })
    }
    if (c.missingBuyerGln) {
      errors.push({
        docKey: c.docKey,
        message: t('validation_summary.error_missing_buyer_gln'),
        xmlTarget: { xpaths: { [dv]: `${re}/Buyer/GLN` } as Partial<Record<DocViewer, string>>, label: 'Buyer/GLN' },
      })
    }
  }

  // Document-referentie ontbreekt/wijkt af tussen aanwezige documenten: WhiteVision
  // processing error (ERROR), niet langer WARNING — zie XMLInsights-SDD
  // requirements/processing-profiles.md REQ-PROCESSING-PROFILES-005 (severity-alignment
  // 2026-09-14). Losstaand van GLN-mismatch (blijft WARNING, zie REQ-PROCESSING-PROFILES-004)
  // en van incomplete-chain (een volledig afwezige stage is geen referentie-finding).
  if (session.ordrsp_desadv_ref_mismatch) {
    const msg = session.desadv_ordrsp_ref
      ? t('validation_summary.error_ordrsp_desadv_ref_mismatch', { desadvRef: session.desadv_ordrsp_ref, ordrspNumber: session.order_response_number ?? '—' })
      : t('validation_summary.error_ordrsp_desadv_ref_missing', { ordrspNumber: session.order_response_number ?? '—' })
    errors.push({
      docKey: 'despatch_advice',
      message: msg,
      xmlTarget: {
        xpaths: { desadv: 'DespatchAdvice/OrderResponseNumber', ordrsp: 'OrderResponse/OrderResponseNumber' },
        label: 'OrderResponseNumber',
      },
    })
  }
  if (session.ordrsp_invoice_ref_mismatch) {
    const msg = session.invoice_ordrsp_ref
      ? t('validation_summary.error_ordrsp_invoice_ref_mismatch', { invoiceRef: session.invoice_ordrsp_ref, ordrspNumber: session.order_response_number ?? '—' })
      : t('validation_summary.error_ordrsp_invoice_ref_missing', { ordrspNumber: session.order_response_number ?? '—' })
    errors.push({
      docKey: 'invoice',
      message: msg,
      xmlTarget: {
        xpaths: { invoice: 'Invoice/OrderResponseNumber', ordrsp: 'OrderResponse/OrderResponseNumber' },
        label: 'OrderResponseNumber',
      },
    })
  }
  if (session.desadv_invoice_ref_mismatch) {
    const msg = session.invoice_desadv_ref
      ? t('validation_summary.error_desadv_invoice_ref_mismatch', { invoiceRef: session.invoice_desadv_ref, desadvNumber: session.desadv_number ?? '—' })
      : t('validation_summary.error_desadv_invoice_ref_missing', { desadvNumber: session.desadv_number ?? '—' })
    errors.push({
      docKey: 'invoice',
      message: msg,
      xmlTarget: {
        xpaths: { invoice: 'Invoice/DespatchAdviceNumber', desadv: 'DespatchAdvice/DespatchAdviceNumber' },
        label: 'DespatchAdviceNumber',
      },
    })
  }
  if (session.supplier_gln_mismatch) {
    const glns = [
      session.ordrsp_supplier_gln ? `${t('validation_summary.gln_prefix_or')}: ${session.ordrsp_supplier_gln}` : null,
      session.desadv_supplier_gln ? `${t('validation_summary.gln_prefix_da')}: ${session.desadv_supplier_gln}` : null,
      session.invoice_supplier_gln ? `${t('validation_summary.gln_prefix_invoice')}: ${session.invoice_supplier_gln}` : null,
    ].filter(Boolean).join(' · ')
    warnings.push({
      message: t('validation_summary.warning_supplier_gln_mismatch', { glns }),
      xmlTarget: {
        xpaths: { ordrsp: 'OrderResponse/Supplier/GLN', desadv: 'DespatchAdvice/Supplier/GLN', invoice: 'Invoice/Supplier/GLN' },
        label: 'Supplier/GLN',
      },
    })
  }
  if (session.project_number_mismatch) {
    // PROJECT_NUMBER_MISMATCH — toon alleen daadwerkelijk aanwezige projectnummers.
    const values = [
      session.ordrsp_project_number  ? `${t('validation_summary.gln_prefix_or')}: ${session.ordrsp_project_number}`        : null,
      session.desadv_project_number  ? `${t('validation_summary.gln_prefix_da')}: ${session.desadv_project_number}`        : null,
      session.invoice_project_number ? `${t('validation_summary.gln_prefix_invoice')}: ${session.invoice_project_number}`  : null,
    ].filter(Boolean).join(' · ')
    warnings.push({
      message: t('validation_summary.warning_project_number_mismatch', { values }),
      xmlTarget: {
        xpaths: { ordrsp: 'OrderResponse/ProjectNumber', desadv: 'DespatchAdvice/ProjectNumber', invoice: 'Invoice/ProjectNumber' },
        label: 'ProjectNumber',
      },
    })
  }
  if (session.buyer_gln_mismatch) {
    const glns = [
      session.ordrsp_buyer_gln ? `${t('validation_summary.gln_prefix_or')}: ${session.ordrsp_buyer_gln}` : null,
      session.desadv_buyer_gln ? `${t('validation_summary.gln_prefix_da')}: ${session.desadv_buyer_gln}` : null,
      session.invoice_buyer_gln ? `${t('validation_summary.gln_prefix_invoice')}: ${session.invoice_buyer_gln}` : null,
    ].filter(Boolean).join(' · ')
    warnings.push({
      message: t('validation_summary.warning_buyer_gln_mismatch', { glns }),
      xmlTarget: {
        xpaths: { ordrsp: 'OrderResponse/Buyer/GLN', desadv: 'DespatchAdvice/Buyer/GLN', invoice: 'Invoice/Buyer/GLN' },
        label: 'Buyer/GLN',
      },
    })
  }

  const errorsByDoc = Object.entries(
    errors.reduce<Record<string, ErrorItem[]>>((acc, item) => {
      ;(acc[item.docKey] ??= []).push(item)
      return acc
    }, {})
  )

  const xsdWarnings = session.xsd_warnings ?? {}
  const xsdEntries = Object.entries(xsdWarnings).filter(([, msgs]) => msgs.length > 0)
  const xsdIssueCount = xsdEntries.reduce((sum, [, msgs]) => sum + msgs.length, 0)

  if (errors.length === 0 && warnings.length === 0 && xsdEntries.length === 0 && missingStages.length === 0)
    return null

  return (
    <div className="space-y-3">
      {missingStages.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-blue-700 dark:text-blue-400 font-semibold text-sm">{t('validation_summary.incomplete_heading')}</span>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {t('validation_summary.incomplete_stages_missing', { stages: missingStages.join(', ') })}
          </p>
          <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
            {t('validation_summary.incomplete_footer')}
          </p>
        </div>
      )}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-red-600 dark:text-red-400 font-semibold text-sm">{t('validation_summary.errors_heading')}</span>
            <span className="bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">{errors.length}</span>
          </div>
          <ul className="space-y-2">
            {errorsByDoc.map(([docKey, items]) => (
              <li key={docKey} className="text-sm text-red-700 dark:text-red-300">
                <span className="flex items-center gap-1.5 font-semibold">
                  <span className="text-red-400">✕</span>
                  {DOC_LABEL[docKey]}
                </span>
                <ul className="mt-1 ml-5 space-y-0.5">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                      <span className="select-none">•</span>
                      <span className="flex-1">{item.message}</span>
                      {item.xmlTarget && onInfoClick && (
                        <InfoButton target={item.xmlTarget} onClick={onInfoClick} />
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-red-500 dark:text-red-400">
            {t('validation_summary.errors_footer', { count: errors.length })}
          </p>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-yellow-700 dark:text-yellow-400 font-semibold text-sm">{t('validation_summary.warnings_heading')}</span>
            <span className="bg-yellow-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">{warnings.length}</span>
          </div>
          <ul className="space-y-1.5">
            {warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-300">
                <span className="mt-0.5 text-yellow-500 shrink-0">⚠</span>
                <span className="flex-shrink">
                  {w.message}
                  {w.xmlTarget && onInfoClick && (
                    <InfoButton target={w.xmlTarget} onClick={onInfoClick} />
                  )}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-yellow-600 dark:text-yellow-500">
            {t('validation_summary.warnings_footer', { count: warnings.length })}
          </p>
        </div>
      )}
      {xsdEntries.length > 0 && (
        <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-orange-700 dark:text-orange-400 font-semibold text-sm">{t('validation_summary.xsd_heading')}</span>
            <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
              {xsdIssueCount}
            </span>
          </div>
          <ul className="space-y-2">
            {xsdEntries.map(([docKey, msgs]) => (
              <li key={docKey} className="text-sm text-orange-800 dark:text-orange-300">
                <span className="font-semibold">{DOC_LABEL[docKey] ?? docKey}</span>
                <ul className="mt-1 ml-4 space-y-0.5">
                  {msgs.map((msg, i) => (
                    <li key={i} className="list-disc text-orange-700 dark:text-orange-400 font-mono text-xs">{msg}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-orange-600 dark:text-orange-500">
            {t('validation_summary.xsd_footer', { count: xsdIssueCount })}
          </p>
        </div>
      )}
    </div>
  )
}
